import range from "lodash/range";

import {
  DatafeedErrorCallback,
  GetMarksCallback,
  HistoryCallback,
  IBasicDataFeed,
  LibrarySymbolInfo,
  Mark,
  OnReadyCallback,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SubscribeBarsCallback,
} from "charting_library";
import { getServerUrl } from "config/backend";
import { SUPPORTED_RESOLUTIONS_V1, SUPPORTED_RESOLUTIONS_V2 } from "config/tradingview";
import { getLimitChartPricesFromStats } from "domain/prices";
import { Bar, FromOldToNewArray } from "domain/tradingview/types";
import {
  formatTimeInBarToMs,
  getCurrentCandleTime,
  multiplyBarValues,
  parseSymbolName,
} from "domain/tradingview/utils";
import { parseError } from "lib/errors";
import { LoadingFailedEvent, LoadingStartEvent, LoadingSuccessEvent, getRequestId, metrics } from "lib/metrics";
import { appendOracleNetworkParam } from "lib/oracleNetwork";
import { PauseableInterval } from "lib/PauseableInterval";
import { sleep } from "lib/sleep";
import {
  getNativeToken,
  getTokenBySymbol,
  getTokenVisualMultiplier,
  getV2Tokens,
  isChartAvailableForToken,
} from "sdk/configs/tokens";
import { isCngnToken } from "sdk/utils/cngnCurrency";
import { parseContractPrice } from "sdk/utils/tokens";

const RESOLUTION_TO_SECONDS = {
  1: 60,
  5: 60 * 5,
  15: 60 * 15,
  60: 60 * 60,
  240: 60 * 60 * 4,
  "1D": 60 * 60 * 24,
  "1W": 60 * 60 * 24 * 7,
  "1M": 60 * 60 * 24 * 30,
};

let metricsRequestId: string | undefined = undefined;
let metricsIsFirstLoadTime = true;
let metricsIsFirstDrawTime = true;

const V1_UPDATE_INTERVAL = 1000;
const V2_UPDATE_INTERVAL_BY_RESOLUTION_MS: Record<string, number> = {
  "1": 2_000,
  "5": 10_000,
  "15": 15_000,
  "60": 30_000,
  "240": 60_000,
  "1D": 120_000,
  "1W": 300_000,
  "1M": 900_000,
};

const SUBSEQUENT_FETCH_MIN_COUNT_BY_RESOLUTION: Record<string, number> = {
  "1": 250,
  "5": 200,
  "15": 160,
  "60": 140,
  "240": 120,
  "1D": 80,
  "1W": 50,
  "1M": 30,
};

const PREFETCH_CANDLES_COUNT = 300;
const MAX_RANGE_CANDLES = 50_000;

const MAX_CANDLES_BY_RESOLUTION: Record<string, number> = {
  "1": 1500,
  "5": 1200,
  "15": 1000,
  "60": 800,
  "240": 600,
  "1D": 500,
  "1W": 300,
  "1M": 200,
};

const CACHE_TTL_BY_RESOLUTION: Record<string, number> = {
  "1": 30_000,
  "5": 60_000,
  "15": 120_000,
  "60": 300_000,
  "240": 600_000,
  "1D": 1_800_000,
  "1W": 3_600_000,
  "1M": 7_200_000,
};

const FIRST_LOAD_LATEST_FALLBACK_SECONDS_BY_RESOLUTION: Record<string, number> = {
  "1": 48 * 60 * 60,
  "5": 7 * 24 * 60 * 60,
  "15": 14 * 24 * 60 * 60,
  "60": 45 * 24 * 60 * 60,
  "240": 120 * 24 * 60 * 60,
  "1D": 400 * 24 * 60 * 60,
  "1W": 5 * 365 * 24 * 60 * 60,
  "1M": 10 * 365 * 24 * 60 * 60,
};

/**
 * Maximum number of consecutive missing bars that will be filled synthetically
 * with flat (close == prev close) candles before the gap is left blank.
 *
 * Higher values keep the 1m chart continuous during brief backend pauses.
 * Lower values for higher timeframes where gaps are less common and large
 * synthetic fills would be visually misleading.
 */
const MAX_SYNTHETIC_GAP_BARS_BY_RESOLUTION: Record<string, number> = {
  "1": 10, // 1m chart  — fill up to 10 minutes of gap synthetically
  "5": 6, // 5m chart  — up to 30 min
  "15": 5, // 15m chart — up to 1h 15m
  "60": 4, // 1h chart  — up to 4h
  "240": 3, // 4h chart
  "1D": 2, // daily
  "1W": 1,
  "1M": 1,
};

function getMaxSyntheticGapBars(resolution: ResolutionString): number {
  return MAX_SYNTHETIC_GAP_BARS_BY_RESOLUTION[resolution] ?? 3;
}
const STRICT_ADDRESS_REGEX = /^0x[0-9a-f]{40}$/;

const PRICE_LINE_GREEN = "#00bd788f";
const PRICE_LINE_RED = "#eb57578f";

function getV2UpdateInterval(resolution: ResolutionString): number {
  return V2_UPDATE_INTERVAL_BY_RESOLUTION_MS[resolution] ?? 15_000;
}

export function getRequiredCandlesForRange(
  from: number | undefined,
  to: number | undefined,
  resolution: ResolutionString
): number | undefined {
  if (from === undefined || to === undefined || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    return undefined;
  }

  const periodSeconds = RESOLUTION_TO_SECONDS[resolution] ?? 60;
  return Math.max(1, Math.ceil((to - from) / periodSeconds) + 2);
}

export function normalizeHistoryCountBack(
  countBack: number | undefined,
  resolution: ResolutionString,
  firstDataRequest?: boolean
): number {
  const parsed = Number(countBack);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.trunc(parsed);
  }

  return firstDataRequest
    ? MAX_CANDLES_BY_RESOLUTION[resolution] ?? PREFETCH_CANDLES_COUNT
    : SUBSEQUENT_FETCH_MIN_COUNT_BY_RESOLUTION[resolution] ?? PREFETCH_CANDLES_COUNT;
}

type CachedBars = {
  bars: FromOldToNewArray<Bar>;
  updatedAt: number;
};

type CandleStaleMeta = {
  latestTimestamp: number | null;
  serverTimestamp: number;
  isStale: boolean;
  staleBySeconds: number;
  staleThresholdSeconds: number;
};

export function shouldTreatSingleBarAsNoData(
  bars: ReadonlyArray<Bar>,
  staleMeta: CandleStaleMeta | undefined
): boolean {
  return bars.length === 1 && staleMeta?.isStale === true;
}

export function shouldFallbackToLatestBars(input: {
  resolution: ResolutionString;
  firstDataRequest?: boolean;
  returnedBarsCount: number;
  to?: number;
  now?: number;
  staleMeta?: CandleStaleMeta;
}): boolean {
  if (!input.firstDataRequest) {
    return false;
  }

  const now = input.now ?? Math.floor(Date.now() / 1000);
  const periodSeconds = RESOLUTION_TO_SECONDS[input.resolution] ?? 60;
  const staleThresholdSeconds = Math.max(input.staleMeta?.staleThresholdSeconds ?? 0, periodSeconds * 3);
  const requestedToIsStale =
    input.to !== undefined && Number.isFinite(input.to) && input.to < now - staleThresholdSeconds;

  if (input.staleMeta?.isStale && (requestedToIsStale || input.returnedBarsCount <= 1)) {
    return true;
  }

  if (input.returnedBarsCount > 0) {
    return false;
  }

  const maxAgeSeconds = FIRST_LOAD_LATEST_FALLBACK_SECONDS_BY_RESOLUTION[input.resolution];
  if (!maxAgeSeconds || input.to === undefined || !Number.isFinite(input.to)) {
    return false;
  }

  return input.to >= now - maxAgeSeconds;
}

export function getHistoryBackfillParams(input: {
  from?: number;
  to?: number;
  countBack?: number;
}): Record<string, string> {
  if (
    input.from === undefined ||
    input.to === undefined ||
    !Number.isFinite(input.from) ||
    !Number.isFinite(input.to) ||
    input.to <= input.from ||
    input.countBack === undefined ||
    !Number.isFinite(input.countBack) ||
    input.countBack <= 0
  ) {
    return {};
  }

  return {
    countBack: String(Math.floor(input.countBack)),
    includeBeforeFrom: "true",
  };
}

export function getLatestFallbackRange(input: {
  latestBar: Bar | undefined;
  periodSeconds: number;
  countBack: number;
  currentFrom: number;
  currentTo: number;
}): { from: number; to: number } {
  if (!input.latestBar) {
    return { from: input.currentFrom, to: input.currentTo };
  }

  const periodSeconds = Math.max(1, Math.floor(input.periodSeconds));
  const countBack = Math.max(1, Math.floor(input.countBack));

  return {
    from: input.latestBar.time - periodSeconds * countBack,
    to: input.latestBar.time,
  };
}

export type TradeMarkDetails = {
  id: string;
  time: number;
  side: "B" | "S";
  account?: string;
  source?: "executed" | "open-position";
  orderType?: number;
  executionType?: number;
  isLong?: boolean;
  executionPrice?: string;
  sizeDeltaUsd?: string;
  txHash?: string;
  marketAddress?: string;
};

export type TradeMarkItem = {
  id?: string | number;
  account?: string;
  orderKey?: string;
  timestamp?: number | string;
  side?: string;
  label?: string;
  source?: "executed" | "open-position";
  orderType?: number | string;
  executionType?: number | string;
  isLong?: boolean;
  executionPrice?: string;
  sizeDeltaUsd?: string;
  txHash?: string;
  marketAddress?: string;
};

export type SyntheticTradeMark = TradeMarkItem & {
  id: string;
  timestamp: number;
  side: "B" | "S";
  source: "executed" | "open-position";
};

export function filterTradeMarkRowsByAccount(rows: TradeMarkItem[], account?: string) {
  const normalizedAccount = account?.trim().toLowerCase();

  if (!normalizedAccount || !STRICT_ADDRESS_REGEX.test(normalizedAccount)) {
    return [];
  }

  return rows.filter((row) => {
    const rowAccount = row?.account ? String(row.account).trim().toLowerCase() : undefined;

    return !rowAccount || rowAccount === normalizedAccount;
  });
}

function normalizeExecutionPrice(rawPrice: string | undefined, tokenDecimals: number | undefined): string | undefined {
  if (!rawPrice) return undefined;

  try {
    const parsed = BigInt(rawPrice);
    if (parsed <= 0n) return undefined;

    if (typeof tokenDecimals === "number" && Number.isFinite(tokenDecimals)) {
      return parseContractPrice(parsed, tokenDecimals).toString();
    }

    return parsed.toString();
  } catch (_error) {
    return undefined;
  }
}

function getTradeMarkMergeKey(item: TradeMarkItem, periodSeconds: number): string | undefined {
  const timestamp = Number(item?.timestamp);
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return undefined;
  }

  const side = String(item?.side ?? item?.label ?? "").toUpperCase();
  if (side !== "B" && side !== "S") {
    return undefined;
  }

  const alignedTime = Math.floor(timestamp / periodSeconds) * periodSeconds;
  const marketAddress = item?.marketAddress ? String(item.marketAddress).toLowerCase() : "";

  return `${marketAddress}:${side}:${alignedTime}`;
}

export function mergeTradeMarkRows(
  executedRows: TradeMarkItem[],
  syntheticRows: TradeMarkItem[],
  periodSeconds: number
) {
  const openPositionMergeKeys = new Set(
    syntheticRows
      .filter((row) => row.source === "open-position")
      .map((row) => getTradeMarkMergeKey(row, periodSeconds))
      .filter((value): value is string => Boolean(value))
  );

  const filteredExecutedRows = executedRows.filter((row) => {
    const mergeKey = getTradeMarkMergeKey(row, periodSeconds);
    if (!mergeKey || !openPositionMergeKeys.has(mergeKey)) {
      return true;
    }

    const hasExecutionType = row?.executionType !== undefined && row?.executionType !== null;
    const executionType = Number(hasExecutionType ? row?.executionType : row?.orderType);
    const isIncrease = hasExecutionType ? executionType === 2 : executionType === 2 || executionType === 3;

    return !isIncrease;
  });

  return [...filteredExecutedRows, ...syntheticRows];
}

export function mergeExecutedTradeMarkRows(backendRows: TradeMarkItem[], syntheticRows: TradeMarkItem[]) {
  const backendOrderKeys = new Set(
    backendRows
      .map((row) => (row?.orderKey ? String(row.orderKey) : undefined))
      .filter((value): value is string => Boolean(value))
  );

  const dedupedSyntheticRows = syntheticRows.filter((row) => {
    const orderKey = row?.orderKey ? String(row.orderKey) : undefined;
    if (!orderKey) {
      return true;
    }

    return !backendOrderKeys.has(orderKey);
  });

  return [...backendRows, ...dedupedSyntheticRows];
}

export function buildTradeMarks(
  rows: TradeMarkItem[],
  params: {
    from: number;
    to: number;
    periodSeconds: number;
    tokenDecimals?: number;
    existingMarkDetails?: Map<string, TradeMarkDetails>;
  }
) {
  const { from, to, periodSeconds, tokenDecimals, existingMarkDetails } = params;
  const marksByBar = new Map<number, number>();
  const nextMarkDetailsById = new Map<string, TradeMarkDetails>(existingMarkDetails);
  const seenMarkIds = new Set<string>();
  const marks: Mark[] = [];

  for (const item of rows) {
    const timestamp = Number(item?.timestamp);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      continue;
    }

    const side = String(item?.side ?? item?.label ?? "").toUpperCase();
    if (side !== "B" && side !== "S") {
      continue;
    }

    const alignedTime = Math.floor(timestamp / periodSeconds) * periodSeconds;
    if (alignedTime < from || alignedTime > to) {
      continue;
    }

    const currentCount = marksByBar.get(alignedTime) ?? 0;
    if (currentCount >= 10) {
      continue;
    }

    const rawMarkId =
      item?.id ??
      item?.orderKey ??
      item?.txHash ??
      `${side}-${timestamp}-${String(item?.orderType ?? "unknown")}-${currentCount}`;
    const markId = String(rawMarkId);

    if (seenMarkIds.has(markId)) {
      continue;
    }

    marksByBar.set(alignedTime, currentCount + 1);
    seenMarkIds.add(markId);

    const isBuy = side === "B";
    const source = item?.source === "open-position" ? "open-position" : "executed";
    const text =
      source === "open-position"
        ? isBuy
          ? "Open long position"
          : "Open short position"
        : isBuy
          ? "Buy executed"
          : "Sell executed";
    marks.push({
      id: markId,
      time: alignedTime,
      color: isBuy ? "green" : "red",
      text,
      label: side,
      labelFontColor: "#FFFFFF",
      minSize: 14,
    });

    nextMarkDetailsById.set(markId, {
      id: markId,
      time: alignedTime,
      side,
      account: item?.account ? String(item.account).toLowerCase() : undefined,
      source,
      orderType: Number.isFinite(Number(item?.orderType)) ? Number(item?.orderType) : undefined,
      executionType: Number.isFinite(Number(item?.executionType)) ? Number(item?.executionType) : undefined,
      isLong: typeof item?.isLong === "boolean" ? item.isLong : undefined,
      executionPrice:
        source === "open-position"
          ? item?.executionPrice
            ? String(item.executionPrice)
            : undefined
          : normalizeExecutionPrice(item?.executionPrice ? String(item.executionPrice) : undefined, tokenDecimals),
      sizeDeltaUsd: item?.sizeDeltaUsd ? String(item.sizeDeltaUsd) : undefined,
      txHash: item?.txHash ? String(item.txHash) : undefined,
      marketAddress: item?.marketAddress ? String(item.marketAddress).toLowerCase() : undefined,
    });
  }

  marks.sort((a, b) => a.time - b.time);

  while (nextMarkDetailsById.size > 5000) {
    const firstKey = nextMarkDetailsById.keys().next().value;
    if (firstKey === undefined) break;
    nextMarkDetailsById.delete(firstKey);
  }

  return {
    marks,
    nextMarkDetailsById,
  };
}

function sanitizeBar(bar: Partial<Bar>): Bar | null {
  const rawTime = Number(bar.time);
  const open = Number(bar.open);
  const high = Number(bar.high);
  const low = Number(bar.low);
  const close = Number(bar.close);

  if (
    !Number.isFinite(rawTime) ||
    !Number.isFinite(open) ||
    !Number.isFinite(high) ||
    !Number.isFinite(low) ||
    !Number.isFinite(close) ||
    open <= 0 ||
    high <= 0 ||
    low <= 0 ||
    close <= 0
  ) {
    return null;
  }

  const time = rawTime > 1_000_000_000_000 ? Math.floor(rawTime / 1000) : Math.floor(rawTime);
  if (time <= 0) {
    return null;
  }

  return {
    time,
    open,
    high: Math.max(high, open, close),
    low: Math.min(low, open, close),
    close,
  };
}

function normalizeBars(bars: FromOldToNewArray<Bar>): FromOldToNewArray<Bar> {
  if (!bars || bars.length === 0) return bars;

  const sanitizedBars = bars.map((bar) => sanitizeBar(bar)).filter((bar): bar is Bar => Boolean(bar));

  if (sanitizedBars.length === 0) {
    return [] as FromOldToNewArray<Bar>;
  }

  const sorted = [...sanitizedBars].sort((a, b) => a.time - b.time);
  const deduped: Bar[] = [];

  for (const bar of sorted) {
    const last = deduped[deduped.length - 1];
    if (!last || bar.time > last.time) {
      deduped.push(bar);
      continue;
    }

    if (bar.time === last.time) {
      deduped[deduped.length - 1] = bar;
    }
  }

  return deduped as FromOldToNewArray<Bar>;
}

function fillBarGapsWithClose(
  bars: FromOldToNewArray<Bar>,
  periodSeconds: number,
  resolution: ResolutionString
): FromOldToNewArray<Bar> {
  if (!bars || bars.length < 2 || periodSeconds <= 0) return bars;

  const maxSyntheticGapBars = getMaxSyntheticGapBars(resolution);
  const sorted = [...bars].sort((a, b) => a.time - b.time);
  const filled: Bar[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    let prev = filled[filled.length - 1];

    if (current.time === prev.time) {
      filled[filled.length - 1] = current;
      continue;
    }

    const missingBars = Math.floor((current.time - prev.time) / periodSeconds) - 1;
    if (missingBars > 0 && missingBars <= maxSyntheticGapBars) {
      while (prev.time + periodSeconds < current.time) {
        const syntheticTime = prev.time + periodSeconds;
        const close = prev.close;
        const synthetic: Bar = {
          time: syntheticTime,
          open: close,
          high: close,
          low: close,
          close,
        };
        filled.push(synthetic);
        prev = synthetic;
      }
    }

    filled.push(current);
  }

  return filled as FromOldToNewArray<Bar>;
}

function parseUnitId(unitId?: string): { visualMultiplier: number; isInverted: boolean } {
  if (!unitId) {
    return { visualMultiplier: 1, isInverted: false };
  }

  const isInverted = unitId.startsWith("i");
  const rawMultiplier = isInverted ? unitId.slice(1) : unitId;
  const visualMultiplier = parseInt(rawMultiplier || "1");

  return {
    visualMultiplier: Number.isFinite(visualMultiplier) ? visualMultiplier : 1,
    isInverted,
  };
}

export function shouldInvertSymbolBars(symbolName: string, unitId?: string): boolean {
  const { isInverted } = parseUnitId(unitId);
  return isInverted || isCngnToken(symbolName);
}

function invertBarValues(bar: Bar): Bar {
  const { open, high, low, close } = bar;
  if (
    !Number.isFinite(open) ||
    !Number.isFinite(high) ||
    !Number.isFinite(low) ||
    !Number.isFinite(close) ||
    open <= 0 ||
    high <= 0 ||
    low <= 0 ||
    close <= 0
  ) {
    return bar;
  }

  return {
    ...bar,
    open: 1 / open,
    close: 1 / close,
    high: 1 / low,
    low: 1 / high,
  };
}

function getQuoteSymbolForChart(chainId: number, baseSymbol: string): string | undefined {
  const tokens = getV2Tokens(chainId) ?? [];

  // Prefer common USD stables, but fall back to any stable if needed.
  const preferred = ["USDT", "USDC", "USDe"];

  for (const sym of preferred) {
    const token = tokens.find((t) => t.isStable && t.symbol === sym);
    if (token && token.symbol !== baseSymbol) {
      return token.symbol;
    }
  }

  const anyStable = tokens.find((t) => t.isStable && t.symbol !== baseSymbol);
  return anyStable?.symbol;
}

export class DataFeed extends EventTarget implements IBasicDataFeed {
  private subscriptions: Record<string, PauseableInterval<Bar | undefined>> = {};
  private resetCacheCallbacks: Record<string, { symbol: string; resolution: ResolutionString; callback: () => void }> =
    {};
  private prefetchedBarsPromises: Record<string, Promise<FromOldToNewArray<Bar>>> = {};
  private barsCache: Map<string, CachedBars> = new Map();
  private candleStaleMetaBySeries: Map<string, CandleStaleMeta> = new Map();
  private markDetailsById: Map<string, TradeMarkDetails> = new Map();
  private visibilityHandler: () => void;
  private marksAccount?: string;
  private marksMarketAddress?: string;
  private syntheticTradeMarks: TradeMarkItem[] = [];

  declare addEventListener: (
    event: "candlesDisplay.success" | "priceLineColor.change" | "candlesStale.change",
    callback: EventListenerOrEventListenerObject
  ) => void;
  declare removeEventListener: (
    event: "candlesDisplay.success" | "priceLineColor.change" | "candlesStale.change",
    callback: EventListenerOrEventListenerObject
  ) => void;

  constructor(
    private chainId: number,
    private oracleBaseUrl: string,
    private tradePageVersion = 2,
    marksContext?: {
      account?: string;
      marketAddress?: string;
    }
  ) {
    super();

    this.setMarksContext(marksContext?.account, marksContext?.marketAddress);

    metrics.startTimer("candlesLoad");
    metrics.startTimer("candlesDisplay");

    this.visibilityHandler = () => {
      if (document.visibilityState === "hidden") {
        this.pauseAll();
      } else {
        this.resumeAll();
      }
    };

    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  setMarksContext(account?: string, marketAddress?: string) {
    const normalizedAccount = account?.trim().toLowerCase();
    const normalizedMarketAddress = marketAddress?.trim().toLowerCase();
    this.marksAccount =
      normalizedAccount && STRICT_ADDRESS_REGEX.test(normalizedAccount) ? normalizedAccount : undefined;
    this.marksMarketAddress =
      normalizedMarketAddress && STRICT_ADDRESS_REGEX.test(normalizedMarketAddress)
        ? normalizedMarketAddress
        : undefined;
    this.markDetailsById.clear();
  }

  setSyntheticTradeMarks(rows?: SyntheticTradeMark[]) {
    this.syntheticTradeMarks = Array.isArray(rows) ? rows : [];
    this.markDetailsById.clear();
  }

  getMarkDetails(markId: string | number): TradeMarkDetails | undefined {
    return this.markDetailsById.get(String(markId));
  }

  private getSeriesKey(symbol: string, resolution: ResolutionString) {
    return `${symbol.toUpperCase()}:${resolution}`;
  }

  private getPrefetchKey(symbol: string, resolution: ResolutionString) {
    return `${symbol.toUpperCase()}:${resolution}`;
  }

  resetBars(symbol?: string, resolution?: ResolutionString) {
    const normalizedSymbol = symbol?.toUpperCase();

    for (const key of Array.from(this.barsCache.keys())) {
      const [, , cachedSymbol, cachedResolution] = key.split(":");
      if (
        (!normalizedSymbol || cachedSymbol === normalizedSymbol) &&
        (!resolution || cachedResolution === resolution)
      ) {
        this.barsCache.delete(key);
      }
    }

    for (const key of Object.keys(this.prefetchedBarsPromises)) {
      const [prefetchedSymbol, prefetchedResolution] = key.split(":");
      if (
        (!normalizedSymbol || prefetchedSymbol === normalizedSymbol) &&
        (!resolution || prefetchedResolution === resolution)
      ) {
        delete this.prefetchedBarsPromises[key];
      }
    }

    for (const key of Array.from(this.candleStaleMetaBySeries.keys())) {
      const [metaSymbol, metaResolution] = key.split(":");
      if ((!normalizedSymbol || metaSymbol === normalizedSymbol) && (!resolution || metaResolution === resolution)) {
        this.candleStaleMetaBySeries.delete(key);
      }
    }

    for (const item of Object.values(this.resetCacheCallbacks)) {
      if ((!normalizedSymbol || item.symbol === normalizedSymbol) && (!resolution || item.resolution === resolution)) {
        try {
          item.callback();
        } catch (_error) {
          // Ignore TradingView reset callback failures; callers still reset chart data explicitly.
        }
      }
    }
  }

  private getStaleMeta(symbol: string, resolution: ResolutionString): CandleStaleMeta | undefined {
    return this.candleStaleMetaBySeries.get(this.getSeriesKey(symbol, resolution));
  }

  private updateStaleMeta(symbol: string, resolution: ResolutionString, meta: CandleStaleMeta) {
    const key = this.getSeriesKey(symbol, resolution);
    const previous = this.candleStaleMetaBySeries.get(key);
    this.candleStaleMetaBySeries.set(key, meta);

    if (!previous || previous.isStale !== meta.isStale || previous.latestTimestamp !== meta.latestTimestamp) {
      this.dispatchEvent(
        new CustomEvent("candlesStale.change", {
          detail: {
            symbol,
            resolution,
            ...meta,
          },
        })
      );
    }
  }

  private resolveStaleMetaFromPayload(
    resolution: ResolutionString,
    candles: FromOldToNewArray<Bar>,
    rawMeta: any
  ): CandleStaleMeta {
    const periodSeconds = RESOLUTION_TO_SECONDS[resolution] ?? 60;
    const fallbackThreshold = Math.max(60, periodSeconds * 3);
    const latestFromCandles = candles.length > 0 ? candles[candles.length - 1].time : null;
    const latestTimestampRaw = Number(rawMeta?.latestTimestamp);
    const latestTimestamp =
      Number.isFinite(latestTimestampRaw) && latestTimestampRaw > 0
        ? Math.floor(latestTimestampRaw)
        : latestFromCandles;
    const serverTimestampRaw = Number(rawMeta?.serverTimestamp);
    const serverTimestamp =
      Number.isFinite(serverTimestampRaw) && serverTimestampRaw > 0
        ? Math.floor(serverTimestampRaw)
        : Math.floor(Date.now() / 1000);
    const thresholdRaw = Number(rawMeta?.staleThresholdSeconds);
    const staleThresholdSeconds =
      Number.isFinite(thresholdRaw) && thresholdRaw > 0 ? Math.floor(thresholdRaw) : fallbackThreshold;
    const staleBySeconds =
      latestTimestamp === null ? staleThresholdSeconds : Math.max(serverTimestamp - latestTimestamp, 0);
    const rawIsStale = rawMeta?.isStale;
    const computedIsStale = latestTimestamp === null || staleBySeconds > staleThresholdSeconds;
    const isStale = typeof rawIsStale === "boolean" ? rawIsStale || computedIsStale : computedIsStale;

    return {
      latestTimestamp,
      serverTimestamp,
      isStale,
      staleBySeconds,
      staleThresholdSeconds,
    };
  }

  searchSymbols(): void {
    // noop
  }

  resolveSymbol(symbolNameWithMultiplier: string, onResolve: ResolveCallback): void {
    let { symbolName, visualMultiplier, isInverted } = parseSymbolName(symbolNameWithMultiplier);

    if (!isChartAvailableForToken(this.chainId, symbolName)) {
      symbolName = getNativeToken(this.chainId).symbol;
      visualMultiplier = 1;
    }

    if (isCngnToken(symbolName)) {
      isInverted = true;
    }

    const token = getTokenBySymbol(this.chainId, symbolName);
    const priceDecimals = token.priceDecimals ?? 2;

    const prefix = visualMultiplier !== 1 ? getTokenVisualMultiplier(token) : "";
    const description = isInverted ? `USD/${symbolName}` : `${prefix}${symbolName}/USD`;

    const symbolInfo: LibrarySymbolInfo = {
      unit_id: `${isInverted ? "i" : ""}${visualMultiplier}`,
      name: symbolName,
      type: "crypto",
      description,
      ticker: symbolName,
      session: "24x7",
      minmov: 1,
      timezone: "Etc/UTC",
      has_intraday: true,
      has_daily: true,
      currency_code: "USD",
      data_status: "streaming",
      visible_plots_set: "ohlc",
      exchange: "NECTAR",
      listed_exchange: "NECTAR",
      format: "price",
      pricescale: Math.max(1, 10 ** priceDecimals / visualMultiplier),
    };

    setTimeout(() => {
      onResolve(symbolInfo);
    }, 0);
  }

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    const { from, to } = periodParams;
    const periodSeconds = RESOLUTION_TO_SECONDS[resolution] ?? 60;
    let effectiveFrom = from;
    let effectiveTo = to;

    const isFirstDraw = metricsIsFirstDrawTime;
    metricsIsFirstDrawTime = false;

    const offset = Math.trunc(Math.max((Date.now() / 1000 - to) / periodSeconds, 0));
    // During a first data request we fetch regular amount of candles
    const requestedCountBack = normalizeHistoryCountBack(
      periodParams.countBack,
      resolution,
      periodParams.firstDataRequest
    );
    const countBack = periodParams.firstDataRequest
      ? requestedCountBack
      : // But for subsequent requests we aggressively fetch more candles so that user can scroll back in time faster
        Math.max(requestedCountBack * 2, SUBSEQUENT_FETCH_MIN_COUNT_BY_RESOLUTION[resolution] ?? 200);
    const requiredRangeCount = getRequiredCandlesForRange(from, to, resolution);
    const requestCount = Math.max(countBack + offset, requiredRangeCount ?? 0);

    const token = getTokenBySymbol(this.chainId, symbolInfo.name);
    const isStable = token.isStable;

    let bars: FromOldToNewArray<Bar> = [];
    try {
      if (!isStable) {
        bars = await this.fetchCandles(symbolInfo.name, resolution, requestCount, false, from, to, false, countBack);
        if (
          shouldFallbackToLatestBars({
            resolution,
            firstDataRequest: periodParams.firstDataRequest,
            returnedBarsCount: bars.length,
            to,
            staleMeta: this.getStaleMeta(symbolInfo.name, resolution),
          })
        ) {
          bars = await this.fetchCandles(
            symbolInfo.name,
            resolution,
            Math.max(countBack, PREFETCH_CANDLES_COUNT),
            false,
            undefined,
            undefined,
            true
          );
          const latestBar = bars[bars.length - 1];
          const fallbackRange = getLatestFallbackRange({
            latestBar,
            periodSeconds,
            countBack,
            currentFrom: effectiveFrom,
            currentTo: effectiveTo,
          });
          effectiveFrom = fallbackRange.from;
          effectiveTo = fallbackRange.to;
        }
      } else {
        const currentCandleTime = getCurrentCandleTime(SUPPORTED_RESOLUTIONS_V2[resolution]);
        bars = this.getStableCandles(currentCandleTime, resolution, requestCount);
        this.updateStaleMeta(symbolInfo.name, resolution, {
          latestTimestamp: currentCandleTime,
          serverTimestamp: Math.floor(Date.now() / 1000),
          isStale: false,
          staleBySeconds: 0,
          staleThresholdSeconds: Math.max(60, (RESOLUTION_TO_SECONDS[resolution] ?? 60) * 3),
        });
      }
    } catch (e) {
      onError(String(e));

      metrics.pushEvent<LoadingFailedEvent>({
        event: "candlesDisplay.failed",
        isError: true,
        time: metrics.getTime("candlesDisplay", true),
        data: {
          requestId: metricsRequestId!,
          isFirstTimeLoad: isFirstDraw,
        },
      });

      return;
    }

    // Filter bars to the requested [from, to] window.
    // Allow a one-period buffer before `from` to include bucket-aligned timestamps
    // (e.g. a 1h candle at floor(from/3600)*3600 that's just before `from`).
    const staleMeta = this.getStaleMeta(symbolInfo.name, resolution);
    const normalizedBaseBars = normalizeBars(bars);
    const normalizedBars = staleMeta?.isStale
      ? normalizedBaseBars
      : fillBarGapsWithClose(normalizedBaseBars, periodSeconds, resolution);
    const barsUpToTo = normalizedBars.filter((bar) => bar.time <= effectiveTo);
    let barsInRange = barsUpToTo.filter((bar) => bar.time >= effectiveFrom - periodSeconds);

    if (barsInRange.length < requestedCountBack && !shouldTreatSingleBarAsNoData(barsUpToTo, staleMeta)) {
      barsInRange = barsUpToTo.slice(-requestedCountBack);
    }

    const barsToReturn: FromOldToNewArray<Bar> = [] as any;
    const { visualMultiplier } = parseUnitId(symbolInfo.unit_id);
    const shouldInvertBars = shouldInvertSymbolBars(symbolInfo.name, symbolInfo.unit_id);
    for (const bar of barsInRange) {
      const nextBar = shouldInvertBars ? invertBarValues(bar) : bar;
      barsToReturn.push(multiplyBarValues(formatTimeInBarToMs(nextBar), visualMultiplier));
    }

    const shouldReturnNoData = shouldTreatSingleBarAsNoData(barsInRange, staleMeta);
    const resultBars = shouldReturnNoData ? ([] as any as FromOldToNewArray<Bar>) : barsToReturn;

    onResult(resultBars, {
      // Consider there is data as long as we have at least one bar.
      // Do not hide the chart just because we have fewer candles than `countBack`,
      // which often happens on fresh markets (e.g. new AssetChain pairs).
      noData: shouldReturnNoData || barsToReturn.length === 0,
    });

    if (metricsIsFirstDrawTime) {
      metrics.pushEvent<LoadingSuccessEvent>({
        event: "candlesDisplay.success",
        isError: false,
        time: metrics.getTime("candlesDisplay", true),
        data: {
          requestId: metricsRequestId!,
          isFirstTimeLoad: isFirstDraw,
        },
      });
    }

    this.dispatchEvent(
      new CustomEvent("candlesDisplay.success", {
        detail: {
          requestId: metricsRequestId!,
          isFirstTimeLoad: isFirstDraw,
        },
      })
    );
  }

  async getMarks(
    symbolInfo: LibrarySymbolInfo,
    from: number,
    to: number,
    onDataCallback: GetMarksCallback<Mark>,
    resolution: ResolutionString
  ): Promise<void> {
    if (!this.marksAccount || !this.marksMarketAddress) {
      onDataCallback([]);
      return;
    }

    const periodSeconds = RESOLUTION_TO_SECONDS[resolution] ?? 60;
    const normalizeUnixSeconds = (value: number) =>
      value > 1_000_000_000_000 ? Math.floor(value / 1000) : Math.floor(value);
    const fromSec = normalizeUnixSeconds(from);
    const toSec = normalizeUnixSeconds(to);

    try {
      const query = new URLSearchParams({
        chainId: String(this.chainId),
        marketAddress: this.marksMarketAddress,
        from: String(fromSec),
        to: String(toSec),
        limit: "500",
      });
      const endpoint = getServerUrl(this.chainId, `/accounts/${this.marksAccount}/trade-marks?${query.toString()}`);

      const response = await fetch(endpoint);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        onDataCallback([]);
        return;
      }

      const rows = Array.isArray(payload?.items) ? payload.items : [];
      const tokenDecimals = getTokenBySymbol(this.chainId, symbolInfo.name)?.decimals;
      const normalizedRows = filterTradeMarkRowsByAccount(
        rows.map((item) => ({
          ...item,
          source: "executed" as const,
          timestamp: normalizeUnixSeconds(Number(item?.timestamp)),
        })),
        this.marksAccount
      );
      const syntheticRowsForCurrentMarket = this.syntheticTradeMarks.filter((row) => {
        const marketAddress = row?.marketAddress ? String(row.marketAddress).toLowerCase() : undefined;
        const rowAccount = row?.account ? String(row.account).toLowerCase() : undefined;

        return marketAddress === this.marksMarketAddress && (!rowAccount || rowAccount === this.marksAccount);
      });
      const syntheticExecutedRows = syntheticRowsForCurrentMarket.filter((row) => row.source === "executed");
      const syntheticOpenPositionRows = syntheticRowsForCurrentMarket.filter((row) => row.source === "open-position");
      const mergedExecutedRows = mergeExecutedTradeMarkRows(normalizedRows, syntheticExecutedRows);
      const mergedRows = mergeTradeMarkRows(mergedExecutedRows, syntheticOpenPositionRows, periodSeconds);
      const { marks, nextMarkDetailsById } = buildTradeMarks(mergedRows, {
        from: fromSec,
        to: toSec,
        periodSeconds,
        tokenDecimals,
        existingMarkDetails: this.markDetailsById,
      });
      this.markDetailsById = nextMarkDetailsById;
      onDataCallback(marks);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Load trade marks failed", error);
      onDataCallback([]);
    }
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    onResetCacheNeededCallback?: () => void
  ): void {
    const token = getTokenBySymbol(this.chainId, symbolInfo.name);
    const isStable = token.isStable;

    const { visualMultiplier } = parseUnitId(symbolInfo.unit_id);
    const shouldInvertBars = shouldInvertSymbolBars(symbolInfo.name, symbolInfo.unit_id);
    const cachedBars = this.getCachedBars(symbolInfo.name, resolution);
    let seededLastReturnedValue = cachedBars?.[cachedBars.length - 1];
    let lastDispatchedPriceLineColor: string | undefined;

    const dispatchPriceLineColor = (bar: Bar) => {
      const nextColor = bar.close >= bar.open ? PRICE_LINE_GREEN : PRICE_LINE_RED;
      if (nextColor === lastDispatchedPriceLineColor) return;

      lastDispatchedPriceLineColor = nextColor;
      this.dispatchEvent(
        new CustomEvent("priceLineColor.change", {
          detail: {
            color: nextColor,
          },
        })
      );
    };

    const interval = new PauseableInterval<Bar | undefined>(
      async ({ lastReturnedValue }) => {
        const effectiveLastReturnedValue = lastReturnedValue ?? seededLastReturnedValue;
        let candlesToFetch = isStable ? 1 : 2;

        const currentCandleTime = getCurrentCandleTime(SUPPORTED_RESOLUTIONS_V2[resolution]);

        if (effectiveLastReturnedValue) {
          const periodSeconds = RESOLUTION_TO_SECONDS[resolution];

          const diff = Math.abs(currentCandleTime - effectiveLastReturnedValue.time);
          if (diff >= periodSeconds) {
            candlesToFetch = Math.max(candlesToFetch, Math.ceil(diff / periodSeconds) + 1);
          }
        }

        let prices: FromOldToNewArray<Bar> = [];

        try {
          prices = !isStable
            ? await this.fetchCandles(symbolInfo.name, resolution, candlesToFetch, false, undefined, undefined, true)
            : this.getStableCandles(currentCandleTime, resolution, candlesToFetch);

          if (isStable) {
            this.updateStaleMeta(symbolInfo.name, resolution, {
              latestTimestamp: currentCandleTime,
              serverTimestamp: Math.floor(Date.now() / 1000),
              isStale: false,
              staleBySeconds: 0,
              staleThresholdSeconds: Math.max(60, (RESOLUTION_TO_SECONDS[resolution] ?? 60) * 3),
            });
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          return lastReturnedValue;
        }

        const periodSeconds = RESOLUTION_TO_SECONDS[resolution] ?? 60;
        const staleMeta = this.getStaleMeta(symbolInfo.name, resolution);
        const normalizedBasePrices = normalizeBars(prices);
        const normalizedPrices = staleMeta?.isStale
          ? normalizedBasePrices
          : fillBarGapsWithClose(normalizedBasePrices, periodSeconds, resolution);
        let newLastReturnedValue: Bar | undefined = effectiveLastReturnedValue;
        let rollingLastReturnedValue: Bar | undefined = effectiveLastReturnedValue;

        let didPatchPreviousCandle = false;

        for (const price of normalizedPrices) {
          if (rollingLastReturnedValue?.time && price.time < rollingLastReturnedValue.time) {
            continue;
          }

          if (
            !staleMeta?.isStale &&
            rollingLastReturnedValue?.time &&
            price.time > rollingLastReturnedValue.time &&
            !didPatchPreviousCandle
          ) {
            didPatchPreviousCandle = true;
            const previousBarWithNewClose = {
              ...rollingLastReturnedValue,
              close: price.open,
              low: Math.min(rollingLastReturnedValue.low, price.open),
              high: Math.max(rollingLastReturnedValue.high, price.open),
            };

            const patchedBar = shouldInvertBars ? invertBarValues(previousBarWithNewClose) : previousBarWithNewClose;
            onTick(multiplyBarValues(formatTimeInBarToMs(patchedBar), visualMultiplier));
            dispatchPriceLineColor(patchedBar);
          }

          const nextBar = shouldInvertBars ? invertBarValues(price) : price;
          onTick(multiplyBarValues(formatTimeInBarToMs(nextBar), visualMultiplier));
          dispatchPriceLineColor(nextBar);

          newLastReturnedValue = price;
          rollingLastReturnedValue = price;
        }

        if (newLastReturnedValue) {
          seededLastReturnedValue = newLastReturnedValue;
        }

        return newLastReturnedValue;
      },
      this.tradePageVersion === 1 ? V1_UPDATE_INTERVAL : getV2UpdateInterval(resolution)
    );

    this.subscriptions[listenerGuid] = interval;
    if (onResetCacheNeededCallback) {
      this.resetCacheCallbacks[listenerGuid] = {
        symbol: symbolInfo.name.toUpperCase(),
        resolution,
        callback: onResetCacheNeededCallback,
      };
    }
  }

  unsubscribeBars(listenerGuid: string): void {
    this.subscriptions[listenerGuid].destroy();
    delete this.subscriptions[listenerGuid];
    delete this.resetCacheCallbacks[listenerGuid];
  }

  onReady(callback: OnReadyCallback): void {
    if (metricsIsFirstLoadTime) {
      metricsRequestId = getRequestId();

      metrics.pushEvent<LoadingStartEvent>({
        event: "candlesDisplay.started",
        isError: false,
        time: metrics.getTime("candlesDisplay"),
        data: {
          requestId: metricsRequestId,
        },
      });
    }

    setTimeout(() => {
      callback({
        supported_resolutions: Object.keys(
          this.tradePageVersion === 1 ? SUPPORTED_RESOLUTIONS_V1 : SUPPORTED_RESOLUTIONS_V2
        ) as ResolutionString[],
        supports_marks: true,
        supports_timescale_marks: false,
        supports_time: true,
      });
    }, 0);
  }

  prefetchBars(symbol: string, resolution: ResolutionString): void {
    const prefetchKey = this.getPrefetchKey(symbol, resolution);
    if (prefetchKey in this.prefetchedBarsPromises) {
      return;
    }

    if (this.getCachedBars(symbol, resolution)) {
      return;
    }

    const count = this.clampCount(resolution, PREFETCH_CANDLES_COUNT);
    this.prefetchedBarsPromises[prefetchKey] = this.fetchCandles(symbol, resolution, count, true);
  }

  private pauseAll() {
    Object.values(this.subscriptions).forEach((subscription) => subscription.pause());
  }

  private resumeAll() {
    Object.values(this.subscriptions).forEach((subscription) => subscription.resume());
  }

  private async fetchCandles(
    symbol: string,
    resolution: ResolutionString,
    count: number,
    isPrefetch = false,
    from?: number,
    to?: number,
    skipCache = false,
    countBack?: number
  ): Promise<FromOldToNewArray<Bar>> {
    const canUseCache = !skipCache && from === undefined && to === undefined;

    if (canUseCache) {
      const cached = this.getCachedBars(symbol, resolution);
      if (cached) {
        return cached;
      }
    }

    const prefetchKey = this.getPrefetchKey(symbol, resolution);
    if (prefetchKey in this.prefetchedBarsPromises && !isPrefetch && metricsIsFirstLoadTime) {
      metricsIsFirstLoadTime = false;
      const promise = this.prefetchedBarsPromises[prefetchKey];
      delete this.prefetchedBarsPromises[prefetchKey];
      const result = await promise;
      const normalized = normalizeBars(result);
      if (!shouldTreatSingleBarAsNoData(normalized, this.getStaleMeta(symbol, resolution))) {
        this.setCachedBars(symbol, resolution, normalized);
      }
      return normalized;
    }

    metricsRequestId = metricsRequestId ?? getRequestId();

    if (isPrefetch) {
      metrics.pushEvent<LoadingStartEvent>({
        event: "candlesLoad.started",
        isError: false,
        time: metrics.getTime("candlesLoad", true),
        data: {
          requestId: metricsRequestId,
          isFirstTimeLoad: isPrefetch,
        },
      });
    }

    metrics.startTimer("candlesLoad");

    count = this.clampCount(resolution, count, from !== undefined || to !== undefined);

    let success = true;
    let result: FromOldToNewArray<Bar> = [];

    if (this.tradePageVersion === 1) {
      result = await Promise.race([
        getLimitChartPricesFromStats(this.chainId, symbol, SUPPORTED_RESOLUTIONS_V1[resolution], count),
        sleep(5000).then(() => Promise.reject("Stats candles timeout")),
      ]).catch((ex) => {
        success = false;
        onCandlesLoadFailed(ex, isPrefetch);
        // eslint-disable-next-line no-console
        console.warn("Load history candles failed", ex);
        return [];
      });
    } else {
      const quoteSymbol = getQuoteSymbolForChart(this.chainId, symbol);
      const symbolsParam = quoteSymbol && quoteSymbol !== symbol ? `${symbol},${quoteSymbol}` : symbol;

      // API 1.1: Use `tokenSymbol` instead of `symbols`
      const searchParams = new URLSearchParams({
        period: SUPPORTED_RESOLUTIONS_V2[resolution],
        limit: String(count),
        // User request: use `symbols` explicitly
        symbols: symbolsParam,
        ...(from !== undefined ? { from: String(Math.floor(from)) } : {}),
        ...(to !== undefined ? { to: String(Math.floor(to)) } : {}),
        ...getHistoryBackfillParams({ from, to, countBack }),
      });

      appendOracleNetworkParam(searchParams, this.chainId);

      result = await Promise.race([
        fetch(`${this.oracleBaseUrl}/prices/candles?` + searchParams.toString())
          .then((res) => res.json())
          .then((json) => {
            const candles = Array.isArray(json?.candles) ? json.candles : [];
            const sanitizedBars: Bar[] = [];

            for (const tuple of candles) {
              if (!Array.isArray(tuple) || tuple.length < 5) {
                continue;
              }

              const parsed = sanitizeBar({
                time: Number(tuple[0]),
                open: Number(tuple[1]),
                high: Number(tuple[2]),
                low: Number(tuple[3]),
                close: Number(tuple[4]),
              });

              if (parsed) {
                sanitizedBars.push(parsed);
              }
            }

            const staleMeta = this.resolveStaleMetaFromPayload(
              resolution,
              sanitizedBars as FromOldToNewArray<Bar>,
              json?.meta
            );
            this.updateStaleMeta(symbol, resolution, staleMeta);

            return sanitizedBars as FromOldToNewArray<Bar>;
          }),
        sleep(5000).then(() => Promise.reject("Oracle candles timeout")),
      ]).catch((ex) => {
        success = false;
        onCandlesLoadFailed(ex, isPrefetch);
        // eslint-disable-next-line no-console
        console.warn("Load history candles failed", ex);
        return [];
      });
    }

    if (success) {
      if (isPrefetch) {
        metrics.pushEvent<LoadingSuccessEvent>({
          event: "candlesLoad.success",
          isError: false,
          time: metrics.getTime("candlesLoad", true),
          data: {
            requestId: metricsRequestId!,
            isFirstTimeLoad: isPrefetch,
          },
        });
      }
    }

    if (success && result.length > 0) {
      const normalized = normalizeBars(result);
      if (canUseCache && !shouldTreatSingleBarAsNoData(normalized, this.getStaleMeta(symbol, resolution))) {
        this.setCachedBars(symbol, resolution, normalized);
      }
      return normalized;
    }

    return result;
  }

  private clampCount(resolution: ResolutionString, count: number, hasExplicitRange = false) {
    const max = hasExplicitRange ? MAX_RANGE_CANDLES : MAX_CANDLES_BY_RESOLUTION[resolution] ?? 1000;
    return Math.min(count, max);
  }

  private getCachedBars(symbol: string, resolution: ResolutionString) {
    const cacheKey = `${this.chainId}:${this.tradePageVersion}:${symbol}:${resolution}`;
    const cached = this.barsCache.get(cacheKey);
    if (!cached) return undefined;

    const ttl = CACHE_TTL_BY_RESOLUTION[resolution] ?? 60_000;
    if (Date.now() - cached.updatedAt > ttl) {
      this.barsCache.delete(cacheKey);
      return undefined;
    }
    return cached.bars;
  }

  private setCachedBars(symbol: string, resolution: ResolutionString, bars: FromOldToNewArray<Bar>) {
    const cacheKey = `${this.chainId}:${this.tradePageVersion}:${symbol}:${resolution}`;
    this.barsCache.set(cacheKey, { bars, updatedAt: Date.now() });

    if (this.barsCache.size > 50) {
      const firstKey = this.barsCache.keys().next().value;
      if (firstKey) this.barsCache.delete(firstKey);
    }
  }

  private getStableCandles(to: number, resolution: ResolutionString, count: number) {
    const periodSeconds = RESOLUTION_TO_SECONDS[resolution];
    return range(count, 0, -1).map((i) => ({
      time: to - i * periodSeconds,
      open: 1,
      close: 1,
      high: 1,
      low: 1,
    }));
  }

  destroy() {
    Object.values(this.subscriptions).forEach((subscription) => subscription.destroy());
    this.resetCacheCallbacks = {};
    document.removeEventListener("visibilitychange", this.visibilityHandler);
  }
}

function onCandlesLoadFailed(ex: any, isPrefetch: boolean) {
  const metricData = parseError(ex);

  metrics.pushEvent<LoadingFailedEvent>({
    event: "candlesLoad.failed",
    isError: true,
    time: metrics.getTime("candlesLoad", true),
    data: {
      requestId: metricsRequestId!,
      isFirstTimeLoad: isPrefetch,
      ...metricData,
    },
  });
}
